import type { Category, Todo } from "../../domain/entities.ts";
import { referenceSections } from "./collection-reference.ts";

export interface InternalTarget {
  key: string;
  title: string;
  kind: "Task" | "Reference";
  href: string;
  preview: string;
  context?: string;
}

/** Only pass entities from the current user's authorized workspace snapshot. */
export function internalTargets(tasks: Todo[], categories: Category[], roots: { id: string; name: string }[] = []): InternalTarget[] {
  const location = (rootId: string, categoryId?: string | null) => [roots.find(root => root.id === rootId)?.name || "Shared space", categoryId ? categories.find(category => category.id === categoryId)?.name || "Unavailable collection" : "No collection"].join(" → ");
  return [
    ...tasks.filter(task => !task.deleted && !task.archivedAt).map(task => ({ key: `task:${task.id}`, title: task.title, kind: "Task" as const, href: `?task=${encodeURIComponent(task.id)}`, preview: task.description.slice(0, 240), context: location(task.rootId, task.categoryId) })),
    ...categories.filter(category => !category.deleted && !category.archivedAt && category.referenceNote?.trim()).flatMap(category => [
      { key: `collection:${category.id}`, title: category.name, kind: "Reference" as const, href: `?collection=${encodeURIComponent(category.id)}&note=introduction`, preview: (category.referenceNote || "").slice(0, 240), context: location(category.rootId, category.id) },
      ...referenceSections(category.referenceNote || "", category.referenceAnchors).filter(section => section.slug !== "introduction").map(section => ({ key: `collection:${category.id}:${section.slug}`, title: `${category.name} · ${section.title}`, kind: "Reference" as const, href: `?collection=${encodeURIComponent(category.id)}&note=${encodeURIComponent(section.slug)}`, preview: section.body.slice(0, 240), context: location(category.rootId, category.id) })),
    ]),
  ];
}

export function internalKey(href: string, location: string): string | null {
  try {
    const url = new URL(href, location);
    const base = new URL(location);
    if (url.origin !== base.origin || url.pathname !== base.pathname) return null;
    const task = url.searchParams.get("task");
    if (task) return `task:${task}`;
    const collection = url.searchParams.get("collection");
    if (!collection) return null;
    const section = url.searchParams.get("note");
    return `collection:${collection}${section && section !== "introduction" ? `:${section}` : ""}`;
  } catch { return null; }
}

/** Match rendered anchors, not raw text: code examples are not references. */
export function referencedKeys(source: string, location: string, render: (source: string) => string, document: Document): Set<string> {
  const template = document.createElement("template");
  template.innerHTML = render(source);
  return new Set(Array.from(template.content.querySelectorAll("a[href]")).flatMap(link => {
    const key = internalKey(link.getAttribute("href")!, location);
    return key ? [key] : [];
  }));
}

export function markdownInternalLink(target: InternalTarget): string {
  // Markdown's intentionally small parser cannot represent nested brackets.
  return `[${target.title.replace(/[\[\]\\\r\n]/g, " ")}](${target.href})`;
}
